import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { CustomDatePicker } from "./custom-date-picker";
import {
  FormField,
  fieldControlClass,
  fieldErrorClass,
  RadioPill,
  RadioCard,
} from "./booking-section/field";
import { toast } from "react-toastify";
import { createQuoteRequest, getGlamPackage } from "../services/api";
import { addHours, format } from "date-fns";
import { cn } from "../lib/util";

interface GlamPackage {
  id: string;
  title: string;
  price: number;
  description: string;
}

// Preferred-time options in 30-minute steps. Value is a 24h "HH:mm" string
// (what the submit handler expects); the label is a friendly 12h time.
const TIME_OPTIONS = (() => {
  const opts: { value: string; label: string }[] = [];
  for (let h = 7; h <= 21; h++) {
    for (const m of [0, 30]) {
      const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      const hour12 = ((h + 11) % 12) + 1;
      const label = `${hour12}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
      opts.push({ value, label });
    }
  }
  return opts;
})();

export function QuoteRequestForm() {
  const navigate = useNavigate();
  const { id: glamPackageId } = useParams<{ id: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [glamPackage, setGlamPackage] = useState<GlamPackage | null>(null);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    glamPackageId: glamPackageId || "",
    glamSpot: "studio_session",
    homeAddress: "",
    landmark: "",
    numberOfFaces: "1",
    firstExperience: "yes",
    additionalInfo: "",
    bookingDate: "",
    bookingTime: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!glamPackageId) {
      toast.error("No package selected.");
      navigate("/booking");
      return;
    }
    let active = true;
    setFormData((prev) => ({ ...prev, glamPackageId }));
    getGlamPackage(glamPackageId)
      .then((res: any) => {
        const d = res?.data?.data;
        if (active && d) setGlamPackage(d);
      })
      .catch(() => {
        /* package context is a nicety — the form still works without it */
      });
    return () => {
      active = false;
    };
  }, [glamPackageId, navigate]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.firstName.trim()) newErrors.firstName = "Please enter your first name.";
    if (!formData.lastName.trim()) newErrors.lastName = "Please enter your last name.";
    if (!formData.email.trim()) {
      newErrors.email = "Please enter your email address.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "That doesn't look like a valid email address.";
    }
    if (!formData.phoneNumber.trim()) newErrors.phoneNumber = "Please enter your phone number.";
    if (formData.glamSpot === "home_service" && !formData.homeAddress.trim()) {
      newErrors.homeAddress = "Please enter your home address.";
    }
    if (!formData.bookingDate) newErrors.bookingDate = "Please choose a preferred date.";
    if (!formData.bookingTime) newErrors.bookingTime = "Please choose a preferred time.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please check the highlighted fields.", { position: "top-center" });
      return;
    }

    try {
      setIsSubmitting(true);
      const bookingStartDateTime = `${formData.bookingDate} ${formData.bookingTime}:00`;
      const startDate = new Date(`${formData.bookingDate}T${formData.bookingTime}:00`);
      const endDate = addHours(startDate, 2);
      const bookingEndDateTime = format(endDate, "yyyy-MM-dd HH:mm:ss");

      const quoteData = {
        name: `${formData.firstName} ${formData.lastName}`,
        email_address: formData.email,
        phone_number: formData.phoneNumber,
        glam_package_id: formData.glamPackageId,
        glam_spot: formData.glamSpot,
        home_address: formData.glamSpot === "home_service" ? formData.homeAddress : null,
        landmark: formData.glamSpot === "home_service" ? formData.landmark : null,
        number_of_faces: formData.numberOfFaces,
        ...(formData.additionalInfo && { additional_info: formData.additionalInfo }),
        booking_start_date: formData.bookingDate,
        booking_end_date: formData.bookingDate,
        booking_start_date_time: bookingStartDateTime,
        booking_end_date_time: bookingEndDateTime,
      };

      await createQuoteRequest(quoteData);
      toast.success("Quote request submitted.");
      navigate("/quote/thank-you");
    } catch (error) {
      console.error("Error submitting quote request:", error);
      toast.error("We couldn't submit your request. Please try again.", {
        position: "top-center",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="overflow-hidden rounded-2xl border border-[#EAD7D6] bg-white"
    >
      {/* Package context */}
      {glamPackage && (
        <div className="flex items-baseline justify-between gap-4 border-b border-[#EAD7D6] bg-[#FBEFEF]/60 px-6 py-5 md:px-10">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-[0.2em] text-[#B87775]">
              Requesting a quote for
            </p>
            <h2 className="mt-1 truncate font-serif text-2xl text-[#3a2e2c]">
              {glamPackage.title}
            </h2>
          </div>
          <span className="shrink-0 font-serif text-xl tabular-nums text-[#B87775]">
            from ${glamPackage.price}
          </span>
        </div>
      )}

      <div className="space-y-10 p-6 md:p-10">
        <p className="text-sm text-[#9A6B69]">
          Fields marked <span className="text-[#B87775]">*</span> are required.
        </p>

        {/* About you */}
        <fieldset className="space-y-6">
          <legend className="mb-1 text-xs uppercase tracking-[0.2em] text-[#B87775]">
            About you
          </legend>

          <div className="grid gap-6 md:grid-cols-2">
            <FormField id="firstName" label="First name" required error={errors.firstName}>
              {(a) => (
                <Input
                  {...a}
                  autoComplete="given-name"
                  placeholder="Amara"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange("firstName", e.target.value)}
                  className={cn(fieldControlClass, errors.firstName && fieldErrorClass)}
                />
              )}
            </FormField>
            <FormField id="lastName" label="Last name" required error={errors.lastName}>
              {(a) => (
                <Input
                  {...a}
                  autoComplete="family-name"
                  placeholder="Okafor"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange("lastName", e.target.value)}
                  className={cn(fieldControlClass, errors.lastName && fieldErrorClass)}
                />
              )}
            </FormField>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <FormField id="email" label="Email address" required error={errors.email}>
              {(a) => (
                <Input
                  {...a}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  className={cn(fieldControlClass, errors.email && fieldErrorClass)}
                />
              )}
            </FormField>
            <FormField id="phone" label="Phone number" required error={errors.phoneNumber}>
              {(a) => (
                <Input
                  {...a}
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="+1 (902) 555-0123"
                  value={formData.phoneNumber}
                  onChange={(e) => handleInputChange("phoneNumber", e.target.value)}
                  className={cn(fieldControlClass, errors.phoneNumber && fieldErrorClass)}
                />
              )}
            </FormField>
          </div>

          <fieldset>
            <legend className="block font-serif text-[17px] leading-snug text-[#3a2e2c]">
              Is this your first glam with Charmé?
              <span className="text-[#B87775]" aria-hidden="true">
                {" "}
                *
              </span>
            </legend>
            <div className="mt-3 flex gap-3">
              <RadioPill
                name="firstExperience"
                value="yes"
                checked={formData.firstExperience === "yes"}
                onChange={() => handleInputChange("firstExperience", "yes")}
              >
                Yes, my first
              </RadioPill>
              <RadioPill
                name="firstExperience"
                value="no"
                checked={formData.firstExperience === "no"}
                onChange={() => handleInputChange("firstExperience", "no")}
              >
                I've been before
              </RadioPill>
            </div>
          </fieldset>
        </fieldset>

        <div className="h-px bg-[#EAD7D6]" />

        {/* Where & how many */}
        <fieldset className="space-y-6">
          <legend className="mb-1 text-xs uppercase tracking-[0.2em] text-[#B87775]">
            Where & how many
          </legend>

          <fieldset>
            <legend className="block font-serif text-[17px] leading-snug text-[#3a2e2c]">
              Where would you like to be glammed?
              <span className="text-[#B87775]" aria-hidden="true">
                {" "}
                *
              </span>
            </legend>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <RadioCard
                name="glamSpot"
                value="studio_session"
                checked={formData.glamSpot === "studio_session"}
                onChange={() => handleInputChange("glamSpot", "studio_session")}
                title="Studio session"
                blurb="Come to our Halifax studio."
              />
              <RadioCard
                name="glamSpot"
                value="home_service"
                checked={formData.glamSpot === "home_service"}
                onChange={() => handleInputChange("glamSpot", "home_service")}
                title="Home service"
                blurb="We come to you, anywhere in the city."
              />
            </div>
          </fieldset>

          {formData.glamSpot === "home_service" && (
            <div className="grid gap-6 rounded-xl border border-[#EAD7D6] bg-[#FBEFEF]/50 p-4 md:grid-cols-2">
              <FormField id="homeAddress" label="Home address" required error={errors.homeAddress}>
                {(a) => (
                  <Input
                    {...a}
                    autoComplete="street-address"
                    placeholder="Street, unit, city"
                    value={formData.homeAddress}
                    onChange={(e) => handleInputChange("homeAddress", e.target.value)}
                    className={cn(fieldControlClass, errors.homeAddress && fieldErrorClass)}
                  />
                )}
              </FormField>
              <FormField
                id="landmark"
                label="Landmark"
                optional
                hint="Anything that helps us find you."
              >
                {(a) => (
                  <Input
                    {...a}
                    placeholder="e.g. across from the park"
                    value={formData.landmark}
                    onChange={(e) => handleInputChange("landmark", e.target.value)}
                    className={fieldControlClass}
                  />
                )}
              </FormField>
            </div>
          )}

          <fieldset>
            <legend className="block font-serif text-[17px] leading-snug text-[#3a2e2c]">
              How many faces are we glamming?
              <span className="text-[#B87775]" aria-hidden="true">
                {" "}
                *
              </span>
            </legend>
            <div className="mt-3 flex flex-wrap gap-2">
              {["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "more"].map((num) => (
                <RadioPill
                  key={num}
                  name="numberOfFaces"
                  value={num}
                  checked={formData.numberOfFaces === num}
                  onChange={() => handleInputChange("numberOfFaces", num)}
                >
                  {num === "more" ? "More than 10" : num}
                </RadioPill>
              ))}
            </div>
          </fieldset>
        </fieldset>

        <div className="h-px bg-[#EAD7D6]" />

        {/* Preferred date & time */}
        <fieldset className="space-y-6">
          <legend className="mb-1 text-xs uppercase tracking-[0.2em] text-[#B87775]">
            Your preferred slot
          </legend>
          <p className="-mt-3 text-sm text-[#8a7a78]">
            Let us know when you'd ideally like your session — we'll confirm
            availability with your quote.
          </p>

          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <p className="font-serif text-[17px] leading-snug text-[#3a2e2c]">
                Preferred date
                <span className="text-[#B87775]" aria-hidden="true">
                  {" "}
                  *
                </span>
              </p>
              <div className="mt-3">
                <CustomDatePicker
                  selectedDate={formData.bookingDate}
                  onDateSelect={(date) => handleInputChange("bookingDate", date)}
                  error={!!errors.bookingDate}
                />
              </div>
              <div aria-live="polite">
                {errors.bookingDate && (
                  <p className="mt-2 text-sm text-[#c0504d]">{errors.bookingDate}</p>
                )}
              </div>
            </div>

            <FormField
              id="bookingTime"
              label="Preferred time"
              required
              error={errors.bookingTime}
            >
              {(a) => (
                <Select
                  value={formData.bookingTime}
                  onValueChange={(value) => handleInputChange("bookingTime", value)}
                >
                  <SelectTrigger
                    id={a.id}
                    aria-invalid={a["aria-invalid"]}
                    aria-describedby={a["aria-describedby"]}
                    aria-required={a["aria-required"]}
                    className={cn(
                      fieldControlClass,
                      "h-auto font-normal data-[placeholder]:text-[#bcaca9]",
                      errors.bookingTime && fieldErrorClass
                    )}
                  >
                    <SelectValue placeholder="Select a time" />
                  </SelectTrigger>
                  <SelectContent className="max-h-64 rounded-xl border-[#EAD7D6]">
                    {TIME_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </FormField>
          </div>
        </fieldset>

        <div className="h-px bg-[#EAD7D6]" />

        {/* Anything else */}
        <fieldset className="space-y-2">
          <legend className="mb-1 text-xs uppercase tracking-[0.2em] text-[#B87775]">
            Anything else
          </legend>
          <label
            htmlFor="additionalInfo"
            className="block font-serif text-[17px] leading-snug text-[#3a2e2c]"
          >
            Tell us about your event
            <span className="ml-1.5 align-middle text-sm italic text-[#9A6B69]/80">
              optional
            </span>
          </label>
          <Textarea
            id="additionalInfo"
            value={formData.additionalInfo}
            onChange={(e) => handleInputChange("additionalInfo", e.target.value)}
            placeholder="The occasion, number of looks, timing, any specific requirements or questions…"
            className={cn(fieldControlClass, "min-h-[120px] resize-y")}
          />
        </fieldset>

        {/* Submit */}
        <div className="space-y-3">
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#B87775] px-6 py-3.5 text-sm font-medium text-white transition hover:bg-[#a96764] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting ? (
              <>
                <svg
                  className="h-4 w-4 animate-spin text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Submitting…
              </>
            ) : (
              "Request my quote"
            )}
          </button>
          <p className="text-center text-sm text-[#9A6B69]">
            We'll reply within 24 hours with pricing and availability.
          </p>
        </div>
      </div>
    </form>
  );
}
